import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { isTradingDay } from '@invest-copilot/shared';
import { isTwOpenNow, isUsOpenNow, type Market } from '../lib/market';
import { fetchJSON } from '../lib/api-client';
import { BuyingPowerSettings, loadBuyingPowerSettings, saveBuyingPowerSettings, type BuyingPower, type BuyingPowerSettingsValue, type Currency, type StrategyProfile } from './BuyingPowerSettings';
import { MiniKLine } from './MiniKLine';
import { computeSurge, surgeMagnitude, isVolBurst, type LiveQuote } from '../lib/surge';
import { IntradayOpportunityPanel } from './IntradayOpportunityPanel';
import { useSymbolName } from '../lib/useSymbolName';
import { limitStatus } from '../lib/limitStatus';
import { LimitTag } from './LimitTag';

type EntryView = 'intraday' | 'plan';

function marketOpenNow(market: Market): boolean {
  return market === 'TW' ? isTwOpenNow() : isUsOpenNow();
}

// 休市日（週末 / 國定假日）：今天完全不交易、沒有「今日新單」，熔斷「暫停推新單」的橫幅顯示無意義。
// 用「休市日」而非「非盤中」——交易日的盤前 / 盤後仍會看今日計畫，熔斷仍有意義、照顯示。
function marketClosedToday(market: Market): boolean {
  return !isTradingDay(market === 'TW' ? 'tw' : 'us');
}

type Action = 'buy' | 'add' | 'hold' | 'reduce' | 'exit' | 'wait' | 'avoid';
type ActionStatus = 'pending-trigger' | 'triggered' | 'adopted' | 'entered' | 'cancelled' | 'expired' | 'stale-data-warning' | 'no-trade';

interface DecisionPoint { point: string; citedFact?: string }
interface Decision {
  symbol: string;
  action: Action;
  entry?: number;
  stop?: number;
  t1?: number;
  t2?: number;
  holdDays?: [number, number];
  expectedReturn?: [number, number];
  confidence: number;
  riskReward?: number;
  reasonsTop3?: DecisionPoint[];
  failureConditions?: string[];
  conflicts?: Array<{ signal: string; verdict: string }>;
  bullPoints?: DecisionPoint[];
  bearPoints?: DecisionPoint[];
  judgeReasoning?: string;
  dataAsOf?: string;
  modelUsed?: string;
  fallbackUsed?: boolean;
  validationFlags?: string[];
  marketProtections?: {
    earningsBlackout?: {
      inBlackout: boolean;
      daysToEarnings?: number;
      earningsDate?: string;
      recommendation: string;
      reason: string;
    };
  };
  riskEvidence?: {
    vol30d?: number;
    vol252d?: number;
    beta?: number;
    maxDrawdown5y?: number;
    avgDailyTurnoverUSD30d?: number;
    nextEarningsDate?: string;
    daysToEarnings?: number;
    earningsDayMoveAvg?: number;
    warnings: string[];
  };
  factsSummary?: {
    catalystCount?: number;
    revenueYoy?: number;
    epsYoy?: number;
    pe?: number;
    forwardPe?: number;
  };
}

interface PositionSizing {
  suggestedCapitalPct: number;
  suggestedNotional: number;
  suggestedQuantity: number;
  quantityUnit: 'share' | 'lot' | 'odd-lot' | 'fractional-share';
  maxLossAmount: number;
  maxLossPctOfPortfolio: number;
  affordability: 'full' | 'reduced-size' | 'unavailable';
  reason: string;
}

interface TradeCost {
  totalRoundTripCostPct: number;
  expectedGrossReturn: [number, number];
  expectedNetReturn: [number, number];
}

export interface ExecutionPlan {
  decision: Decision;
  positionSizing: PositionSizing;
  tradeCost: TradeCost;
  actionStatus: ActionStatus;
  executionSummary: string;
  warnings: string[];
  entryPlan?: {
    timing: string;
    trigger: string;
    invalidBeforeEntry: string;
    orderType: string;
    limitPrice?: number;
  };
}

type ExecutionPlanSource = 'portfolio' | 'new' | 'watchlist' | 'self-watchlist';

interface CircuitBreakerState {
  enabled: boolean;
  status: 'normal' | 'halt-new-trades' | 'manual-override';
  reason?: string;
}

interface DailyPlan {
  date: string;
  marketAction: 'trade' | 'no-trade' | 'manage-existing-only';
  reason: string;
  buyingPower: BuyingPower;
  portfolioActions: ExecutionPlan[];
  newPicks: ExecutionPlan[];
  watchlist: ExecutionPlan[];
  /** 進場機會掃描補位池（含 avoid、比 watchlist 大）；舊 cache 無此欄位時 fallback watchlist。 */
  scanFillers?: ExecutionPlan[];
  expectedNetReturnRange?: [number, number];
  cashAfterPlannedTrades: Partial<Record<Currency, number>>;
  warnings: string[];
  circuitBreaker?: CircuitBreakerState;
}

interface ThesisForHitRate {
  id: string;
  status: string;
  symbol?: string;
  actualEntry?: number;
  entry?: number;
  finalPnlPct?: number;
  realizedPnl?: number;
}

interface ThesesResponse { theses: ThesisForHitRate[]; count: number }

// /api/trade-plans/summary 的精簡型別（命中率唯一準據，僅取 hero 需要的欄位）。
interface TradePlanSummaryRow { closed: number; win: number }
interface TradePlanSummaryResp { rows: TradePlanSummaryRow[] }

type LensId = 'hot' | 'cold' | 'wr05up' | 'wr05down';

interface LensCandidate {
  symbol: string;
  name?: string;
  price: number;
  changePct: number;
  lensScore: number;
  /** 後端算好的純技術信心度（computeFactsConfidence，連續、不卡 55%）。舊版 fallback 用 lensScore/15。 */
  confidence?: number;
  scores: Partial<Record<'1m' | '5m' | 'daily', { score: number; signal: string }>>;
  notes: string[];
  suggestedLevels?: { entry: number; stop: number; t1: number; t2: number };
  dataSource: 'cache' | 'twse' | 'tpex' | 'yahoo' | 'mixed';
  delayed: boolean;
  asOf: string;
}

interface LensScanResult {
  lensId: LensId;
  market: 'tw' | 'us';
  asOf: string;
  fromCache: boolean;
  refreshing?: boolean;
  warnings?: string[];
  universe?: { total: number; max: number; counts?: Record<string, number>; asOf?: string };
  candidates: LensCandidate[];
}

const LENS_BUTTONS: Array<{ id: LensId; label: string; title: string }> = [
  { id: 'hot', label: '🔥 飆股', title: '飆股潛力：技術分 + 量能 + 過熱扣分' },
  { id: 'cold', label: '❄ 超跌', title: '超跌續弱：負向技術分 + 量能 + 過冷扣分' },
  { id: 'wr05up', label: '↗ 勝率', title: '+0.5% historical proxy 勝率 TOP' },
  { id: 'wr05down', label: '↘ 賠率', title: '-0.5% downside proxy TOP' },
];

function marketToApi(market: Market): 'tw' | 'us' {
  return market === 'TW' ? 'tw' : 'us';
}

function isTaiwanSymbol(symbol: string): boolean {
  return symbol.endsWith('.TW') || symbol.endsWith('.TWO');
}

function currencyForSymbol(symbol: string): Currency {
  return isTaiwanSymbol(symbol) ? 'TWD' : 'USD';
}

function money(n: number | undefined, currency: Currency): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: currency === 'TWD' ? 0 : 2 })}`;
}

// 純金額（無幣別前綴）：台美已分區顯示，metrics 不重複寫 USD/TWD 省一行。
function amount(n: number | undefined, currency: Currency): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: currency === 'TWD' ? 0 : 2 });
}

// expectedReturn / expectedNetReturn 已是「百分點」（9.19 = 9.19%），不要再 * 100。
function pctPoints(n: number | undefined, digits = 1): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function price(n: number | undefined, symbol?: string): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  // 台股報價最小跳動到 0.5（1 位小數即可）；美股 2 位小數。
  const digits = symbol && isTaiwanSymbol(symbol) ? 1 : 2;
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatLensTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-TW', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function quantityUnit(unit: PositionSizing['quantityUnit']): string {
  if (unit === 'odd-lot') return '零股';
  if (unit === 'lot') return '張';
  if (unit === 'fractional-share') return '碎股';
  return '股';
}

function strategyLabel(profile: StrategyProfile): string {
  if (profile === 'day-swing') return '短線';
  if (profile === 'swing') return '波段';
  return '保守';
}

function actionLabel(action: Action): string {
  const map: Record<Action, string> = {
    buy: '買進',
    add: '加碼',
    hold: '續抱',
    reduce: '減碼',
    exit: '出場',
    wait: '觀望',
    avoid: '避開',
  };
  return map[action];
}

function displayStatusLabel(action: Action, status: ActionStatus): string {
  if (action === 'wait' && status === 'pending-trigger') return '觀察中';
  if (action === 'avoid' && status === 'no-trade') return '不碰';
  // 偏多（買進／加碼）但被信心門檻擋下 → 明說「訊號不足」，避免與「買進」並列看起來自相矛盾。
  if ((action === 'buy' || action === 'add') && status === 'no-trade') return '訊號不足';
  return statusLabel(status);
}

function displayExecutionSummary(plan: ExecutionPlan): string {
  const action = plan.decision.action;
  if (plan.positionSizing.reason === '自選觀察，不是可下單計畫。') return plan.executionSummary;
  if (action === 'wait') return '觀望：條件還沒成形，暫不下單。';
  if (action === 'avoid') return '避開：風險或條件不符合，暫不下單。';
  return plan.executionSummary;
}

function warningLabel(warning: string): string {
  const map: Record<string, string> = {
    'stale-data-warning': '資料新鮮度警告',
    'prompt-store-unavailable': 'Prompt hot-reload fallback',
    'llm-fallback-used': 'LLM fallback 已使用',
    'market-risk-off': '大盤 risk-off',
    'circuit-breaker': '安全暫停推單',
  };
  return map[warning] ?? warning;
}

// 把後端 warning code 轉成「使用者看得懂的短語 + 滑鼠懸停解釋」。
// 例：calibration-low-sample → 「命中率樣本偏少」+ tooltip 說明。
function flagDisplay(warning: string): { label: string; title: string } {
  const map: Record<string, { label: string; title: string }> = {
    'stale-data-warning': { label: '資料偏舊', title: '部分報價 / 指標的更新時間較舊，請留意進場時點。' },
    'prompt-store-unavailable': { label: 'Prompt 用預設版', title: 'Prompt 線上熱更新暫時讀不到，改用程式內建版本，不影響判讀。' },
    'market-risk-off': { label: '大盤偏空', title: '大盤目前 risk-off，整體環境偏空，控制部位、別追高。' },
    'circuit-breaker': { label: '安全暫停推單', title: '偵測到資料異常或近期連續虧損，暫時不推新單以策安全。' },
    'calibration-low-sample': { label: '命中率樣本偏少', title: '近期已結案的交易計畫數量還不多，顯示的命中率 / 信心校準參考性較低，多累積幾筆後會更準。' },
  };
  // llm-fallback-used 改在 hero 直接顯示實際 model（見 modelInUse），這裡不再列為警告。
  return map[warning] ?? { label: warning, title: warning };
}

// 從今日計畫的各 ExecutionPlan 取「實際用了哪個模型」做 hero 顯示（取第一個非 facts-only 的 modelUsed）。
function modelInUse(plan: DailyPlan | undefined): string | null {
  if (!plan) return null;
  const all = [...(plan.portfolioActions ?? []), ...(plan.newPicks ?? []), ...(plan.watchlist ?? [])];
  for (const p of all) {
    const m = p.decision.modelUsed;
    if (m && !m.startsWith('facts-only')) return m;
  }
  // 全部都是 facts-only（LLM 額度用完，走技術骨架）
  if (all.some((p) => p.decision.modelUsed?.startsWith('facts-only'))) return 'facts-only（技術骨架）';
  return null;
}

function uiText(value: string | undefined): string | undefined {
  return value
    ?.replace(/stale-data-warning-over-3-in-24h/gi, '24 小時內資料過舊警告超過 3 次')
    .replace(/recent safety metrics tripped/gi, '近期安全指標觸發')
    .replace(/Circuit breaker/gi, '安全暫停推單')
    .replace(/thesis/gi, '交易計畫')
    .replace(/stale-data-warning/gi, '資料新鮮度警告')
    .replace(/ai_unavailable/gi, 'AI 暫時不可用');
}

function circuitBreakerExplanation(reason: string | undefined): string {
  // 後端在 manual reset 後會把 reason 改成 "manual reset: <user-supplied tag>"，
  // 那是 audit 用的內部值；如果不小心被 banner 讀到，至少不要 leak 出去。
  const cleaned = reason?.replace(/^manual reset:\s*/i, '').trim();
  const readable = uiText(cleaned || reason) ?? '近期安全指標被觸發';
  return `企鵝偵測到近期資料異常（例如報價連續過舊、AI 服務不穩定、近期連續虧損），暫時不推今日新單，避免照壞資料下決策。觸發條件：${readable}。如果你已經確認資料源正常，可以按右邊「我已確認資料，恢復推單」恢復。`;
}

type BreakerEvent = {
  at: string;
  type: 'stale-data-warning' | 'ai_unavailable';
  source?: string;
  detail?: string;
};

function eventTypeLabel(type: BreakerEvent['type']): string {
  if (type === 'stale-data-warning') return '資料新鮮度警告';
  if (type === 'ai_unavailable') return 'AI 服務不穩定';
  return type;
}

function formatBreakerTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-TW', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function BreakerEventsDisclosure({ tone }: { tone: 'down' | 'warn' }) {
  const [open, setOpen] = useState(false);
  const events = useQuery({
    queryKey: ['breaker-events'],
    queryFn: () => fetchJSON<{ events: BreakerEvent[] }>(`/api/circuit-breaker/events?dedupe=true`),
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });
  const colorClass = tone === 'down' ? 'text-(--down)' : 'text-(--warn)';
  return (
    <div className="mt-2 text-[11px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 ${colorClass} hover:underline`}
      >
        {open ? '收起觸發紀錄' : '查看觸發紀錄（近 24 小時）'}
      </button>
      {open && (
        <div className="mt-1 rounded border border-(--border) bg-(--bg-panel) px-2 py-1.5 text-[11px] text-(--text-secondary)">
          {events.isLoading && <div>讀取中…</div>}
          {events.isError && <div className="text-(--warn)">讀取失敗：{(events.error as Error).message}</div>}
          {events.data && events.data.events.length === 0 && (
            <div>近 24 小時沒有記錄任何觸發事件。</div>
          )}
          {events.data && events.data.events.length > 0 && (
            <>
              <div className="mb-1 text-[10px] text-(--text-muted)">
                顯示去重後的觸發紀錄（相同 type / source / 標的只列一次）；若要查源頭真實出處可直接查 Cosmos cache key <code>circuit-breaker:events:YYYY-MM-DD</code>。
              </div>
              <ul className="space-y-0.5">
                {events.data.events.slice(0, 30).map((e, i) => (
                  <li key={i} className="flex flex-wrap gap-x-2 gap-y-0.5">
                    <span className="text-(--text-muted)">{formatBreakerTime(e.at)}</span>
                    <span className="font-semibold">{eventTypeLabel(e.type)}</span>
                    {e.source && <span className="text-(--text-muted)">來源：{e.source}</span>}
                    {e.detail && <span>標的：{e.detail}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function selfWatchlistPlan(symbol: string): ExecutionPlan {
  const currency = currencyForSymbol(symbol);
  return {
    decision: {
      symbol,
      action: 'wait',
      confidence: 0,
      reasonsTop3: [{ point: '自選觀察：尚未進入今日 AI 精選清單；保留在觀望清單方便追蹤。' }],
      failureConditions: ['若今日行動或 Evidence 出現明確進場條件，再重新評估。'],
    },
    positionSizing: {
      suggestedCapitalPct: 0,
      suggestedNotional: 0,
      suggestedQuantity: 0,
      quantityUnit: isTaiwanSymbol(symbol) ? 'odd-lot' : 'share',
      maxLossAmount: 0,
      maxLossPctOfPortfolio: 0,
      affordability: 'unavailable',
      reason: '自選觀察，不是可下單計畫。',
    },
    tradeCost: {
      totalRoundTripCostPct: 0,
      expectedGrossReturn: [0, 0],
      expectedNetReturn: [0, 0],
    },
    actionStatus: 'pending-trigger',
    executionSummary: `自選觀察：${symbol} 尚未成為今日新單，先放觀望。`,
    warnings: [],
    entryPlan: {
      timing: 'pullback',
      trigger: '等待 Decision Engine 或 Evidence 出現明確條件',
      invalidBeforeEntry: '條件未成形前不下單',
      orderType: 'limit',
    },
  } satisfies ExecutionPlan;
}

function mergeWatchlistPlans(plan: DailyPlan | undefined, watchlistSymbols: string[]): ExecutionPlan[] {
  // 後端 decisions/today 已對自選股跑 decideForSymbol；LLM exhausted 時走 facts-only fallback
  // (lib/facts-only-decision.ts) 仍會回有 entry/stop/T1/T2 的 Decision。
  // 但因為 dailyPlan 用 keepPreviousData，自選剛刪光時 plan.watchlist 可能還是「上一次的舊自選」，
  // 所以一律用「前端目前的 watchlistSymbols」過濾 — 自選刪光 → 回空陣列，進場機會只剩 AI 推薦。
  const allow = new Set(watchlistSymbols.map((s) => s.toUpperCase()));
  if (allow.size === 0) return [];
  return (plan?.watchlist ?? []).filter((p) => allow.has(p.decision.symbol.toUpperCase()));
}

const LENS_DIRECTION: Record<LensId, 'long' | 'short' | 'wait'> = {
  hot: 'long',
  cold: 'wait',
  wr05up: 'long',
  wr05down: 'wait',
};

const LENS_LABEL: Record<LensId, string> = {
  hot: '🔥 飆股',
  cold: '❄ 超跌',
  wr05up: '↗ 勝率',
  wr05down: '↘ 賠率',
};

/**
 * 把 LensCandidate 轉成 ExecutionPlan，跟 newPicks/觀望清單共用 ActionCard 渲染。
 * confidence = lensScore / 15 clamp 至 0.3~0.55（明示「facts-only、無 LLM judge」）。
 * action：hot/wr05up → buy（但被後端 isFactsOnly 篩成 watchlist 級），cold/wr05down → wait。
 */
function lensCandidateToPlan(c: LensCandidate, lensId: LensId): ExecutionPlan {
  const dir = LENS_DIRECTION[lensId];
  const action: Decision['action'] = dir === 'long' ? 'buy' : 'wait';
  // 信心優先用後端 computeFactsConfidence（連續、不卡 55%）；舊 cache 無 confidence 時退回 lensScore/15。
  const conf = typeof c.confidence === 'number'
    ? c.confidence
    : Math.max(0.3, Math.min(0.55, c.lensScore / 15));
  const currency = currencyForSymbol(c.symbol);
  const tw = isTaiwanSymbol(c.symbol);
  return {
    decision: {
      symbol: c.symbol,
      action,
      entry: c.suggestedLevels?.entry,
      stop: c.suggestedLevels?.stop,
      t1: c.suggestedLevels?.t1,
      t2: c.suggestedLevels?.t2,
      confidence: conf,
      reasonsTop3: [
        { point: `L1 Lens ${LENS_LABEL[lensId]}：lensScore ${c.lensScore.toFixed(1)}` },
        ...(c.notes.slice(0, 2).map((n) => ({ point: n }))),
      ],
      failureConditions: ['純技術分數，無 AI 文字判讀 — 進場前再對照即時新聞 / 風控。'],
      modelUsed: `facts-only:lens:${lensId}`,
      validationFlags: ['facts-only-fallback', `lens:${lensId}`],
      dataAsOf: c.asOf,
    } as Decision,
    positionSizing: {
      suggestedCapitalPct: 0,
      suggestedNotional: 0,
      suggestedQuantity: 0,
      quantityUnit: tw ? 'lot' : 'share',
      maxLossAmount: 0,
      maxLossPctOfPortfolio: 0,
      affordability: 'unavailable',
      reason: 'L1 Lens 探索：未經 LLM 判讀也未跑購買力，按「採納」會寫入交易計畫並用 suggestedLevels 當預設。',
    },
    tradeCost: {
      totalRoundTripCostPct: 0,
      expectedGrossReturn: [0, 0],
      expectedNetReturn: [0, 0],
    },
    actionStatus: 'pending-trigger',
    executionSummary: `${LENS_LABEL[lensId]} 候選 (${currency} ${c.price.toFixed(2)}, ${c.changePct >= 0 ? '+' : ''}${c.changePct.toFixed(2)}%)`,
    warnings: c.delayed ? ['資料延遲'] : [],
  };
}

/**
 * 把 IntradayDecision 轉成 ExecutionPlan。intraday-opportunities timer 已經跑過 LLM judge，
 * action / conf / levels 皆可用，conf 通常較高（0.5~0.85）。
 */
function intradayDecisionToPlan(d: {
  symbol: string;
  action: Decision['action'];
  entry?: number;
  stop?: number;
  t1?: number;
  t2?: number;
  confidence: number;
  reasonsTop3?: DecisionPoint[];
  judgeReasoning?: string;
  asOf?: string;
}): ExecutionPlan {
  const tw = isTaiwanSymbol(d.symbol);
  return {
    decision: {
      symbol: d.symbol,
      action: d.action,
      entry: d.entry,
      stop: d.stop,
      t1: d.t1,
      t2: d.t2,
      confidence: d.confidence,
      reasonsTop3: d.reasonsTop3 ?? [],
      failureConditions: [],
      judgeReasoning: d.judgeReasoning,
      modelUsed: 'intraday-opportunity',
      dataAsOf: d.asOf ?? new Date().toISOString(),
    } as Decision,
    positionSizing: {
      suggestedCapitalPct: 0,
      suggestedNotional: 0,
      suggestedQuantity: 0,
      quantityUnit: tw ? 'lot' : 'share',
      maxLossAmount: 0,
      maxLossPctOfPortfolio: 0,
      affordability: 'unavailable',
      reason: '盤中即時：中-LLM 判讀僅標方向與價位，購買力張數請從「今日計畫」採納。',
    },
    tradeCost: {
      totalRoundTripCostPct: 0,
      expectedGrossReturn: [0, 0],
      expectedNetReturn: [0, 0],
    },
    actionStatus: d.action === 'buy' || d.action === 'add' ? 'pending-trigger' : 'pending-trigger',
    executionSummary: '盤中即時·中-LLM 判讀',
    warnings: [],
  };
}

/**
 * 合併 intraday LLM Top 5 + lens candidates 成單一排行榜。
 * - 同 symbol 兩邊都有 → 用 LLM 為主（conf 高），但 reasonsTop3 加一條 lens 命中提示
 * - 排序：先看是否有 LLM（有的在上），再看 confidence desc
 */
function unifyOpportunities(
  intradayPlans: ExecutionPlan[],
  lensPlans: ExecutionPlan[],
): ExecutionPlan[] {
  const bySym = new Map<string, ExecutionPlan>();
  for (const p of intradayPlans) bySym.set(p.decision.symbol, p);
  for (const lp of lensPlans) {
    const exist = bySym.get(lp.decision.symbol);
    if (exist) {
      // 兩邊都有：在 LLM 卡的 reasonsTop3 加一條 lens 命中
      const lensReason = lp.decision.reasonsTop3?.[0]?.point;
      if (lensReason) {
        bySym.set(lp.decision.symbol, {
          ...exist,
          decision: {
            ...exist.decision,
            reasonsTop3: [
              ...(exist.decision.reasonsTop3 ?? []),
              { point: `也在 ${lensReason}` },
            ].slice(0, 4),
          },
        });
      }
    } else {
      bySym.set(lp.decision.symbol, lp);
    }
  }
  return Array.from(bySym.values()).sort((a, b) => {
    const aLlm = a.decision.modelUsed === 'intraday-opportunity' ? 1 : 0;
    const bLlm = b.decision.modelUsed === 'intraday-opportunity' ? 1 : 0;
    if (aLlm !== bLlm) return bLlm - aLlm;
    return (b.decision.confidence ?? 0) - (a.decision.confidence ?? 0);
  }).slice(0, 8); // 上限 8 檔：讓盤中即時 L1（lens）候選都能進 picks，配 buildOppLayout 補滿 4×2；LLM timer 空時也不等。
}

interface IntradayOpportunityApiResp {
  available: boolean;
  market?: 'tw' | 'us';
  asOf?: string;
  decisions?: Array<{
    symbol: string;
    action: Decision['action'];
    entry?: number;
    stop?: number;
    t1?: number;
    t2?: number;
    confidence: number;
    reasonsTop3?: DecisionPoint[];
    judgeReasoning?: string;
  }>;
}

export function TodayActionPanel({
  market,
  watchlistSymbols = [],
  portfolioSymbols = [],
  onSelectSymbol,
  activeSymbol,
  onActiveDecisionChange,
}: {
  market: Market;
  watchlistSymbols?: string[];
  portfolioSymbols?: string[];
  onSelectSymbol: (symbol: string) => void;
  /** 研究台當前看的股票；TodayActionPanel 依此自動找該股的 AI decision 回報給研究台頂部結論細條。 */
  activeSymbol?: string;
  /** 回報 active 股的 AI decision（找不到回 null）；研究台用它預設顯示結論細條 + 分流 AI 依據，不再需要手動按鈕。 */
  onActiveDecisionChange?: (plan: ExecutionPlan | null) => void;
}) {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<BuyingPowerSettingsValue>(() => loadBuyingPowerSettings());
  const [bpOpen, setBpOpen] = useState(false);
  // 需求 2：「技術快篩」預設用「勝率」(wr05up)，不是飆股(hot)。
  const [activeLens, setActiveLens] = useState<LensId | null>('wr05up');
  const [adoptedLensSymbol, setAdoptedLensSymbol] = useState<string | null>(null);
  // 進場機會檢視：盤中預設看「盤中即時」訊號，休市看「今日計畫」（含可執行張數）。
  const [entryView, setEntryView] = useState<EntryView>(() => (marketOpenNow(market) ? 'intraday' : 'plan'));
  // 切換市場時，回到該市場的智慧預設檢視。
  useEffect(() => {
    setEntryView(marketOpenNow(market) ? 'intraday' : 'plan');
  }, [market]);
  // 使用者按「重新整理」時設 true；次一次 queryFn 會帶 force=true 讓後端跳過 cache，然後重置。
  const forceNextRefetch = useRef(false);

  function updateSettings(next: BuyingPowerSettingsValue) {
    setSettings(next);
    saveBuyingPowerSettings(next);
  }

  const dailyPlan = useQuery({
    queryKey: ['daily-plan', marketToApi(market), settings, watchlistSymbols.join(','), portfolioSymbols.join(',')],
    queryFn: () => {
      const force = forceNextRefetch.current;
      forceNextRefetch.current = false;
      return fetchJSON<DailyPlan>('/api/decisions/today', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market: marketToApi(market),
          limit: 8,
          buyingPower: settings.buyingPower,
          strategyProfile: settings.strategyProfile,
          watchlistSymbols,
          portfolioSymbols,
          force,
        }),
      });
    },
    // 盤中每 5 分鐘重抓；後端 universe 快取 10 分鐘 + per-symbol decision 按 facts.asOf
    // 快取，所以多數 refetch 不會真的重跑 LLM，只有 facts 更新才會。
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 1,
    // 自選/設定異動：保留上一次資料避免今日行動瞬間空白。
    // 但「切 market」時不保留（回 undefined）→ 顯示骨架框架，不要顯示舊市場的舊卡很久。
    placeholderData: (prev, prevQuery) =>
      prevQuery && (prevQuery.queryKey as unknown[])[1] === marketToApi(market) ? prev : undefined,
  });

  // skeleton：facts-only（不打 LLM）的今日計畫，秒回。讓「卡片框先一口氣出來」，
  // 不用空等整包 LLM 跑完；全量 dailyPlan 一回來就接手替換（內容慢慢浮現）。
  const skeletonPlan = useQuery({
    queryKey: ['daily-plan-skeleton', marketToApi(market), settings, watchlistSymbols.join(','), portfolioSymbols.join(',')],
    queryFn: () =>
      fetchJSON<DailyPlan>('/api/decisions/today/skeleton', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market: marketToApi(market),
          limit: 20,
          buyingPower: settings.buyingPower,
          strategyProfile: settings.strategyProfile,
          watchlistSymbols,
          portfolioSymbols,
        }),
      }),
    staleTime: 5 * 60_000,
    retry: 1,
    placeholderData: (prev, prevQuery) =>
      prevQuery && (prevQuery.queryKey as unknown[])[1] === marketToApi(market) ? prev : undefined,
  });

  const closedTheses = useQuery({
    queryKey: ['theses', 'closed', 7],
    queryFn: () => fetchJSON<ThesesResponse>('/api/theses?status=closed&days=7'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  // 🐧 企鵝命中：以 /api/trade-plans/summary 為命中率唯一準據（copilot-instructions §6.1），
  // 與「近期績效」(closed theses 7天) 是不同指標 — 這裡取近 30 天全體 TradePlan 勝率。
  const tradePlanSummary = useQuery({
    queryKey: ['trade-plan-summary', 30, 'confidenceBucket', 'hero'],
    queryFn: () => fetchJSON<TradePlanSummaryResp>('/api/trade-plans/summary?days=30&groupBy=confidenceBucket'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  // 我的部位實際買入均價：用來在迷你 K 線標記「我買在哪」(需求 4)。
  const openTheses = useQuery({
    queryKey: ['theses', 'open'],
    queryFn: () => fetchJSON<ThesesResponse>('/api/theses?status=open'),
    staleTime: 60_000,
    retry: false,
  });
  const costBySymbol = useMemo(() => {
    const map: Record<string, number> = {};
    for (const t of openTheses.data?.theses ?? []) {
      const cost = t.actualEntry ?? t.entry;
      if (t.symbol && typeof cost === 'number' && cost > 0) map[t.symbol] = cost;
    }
    return map;
  }, [openTheses.data]);

  const lensScan = useQuery({
    queryKey: ['lens-scan', marketToApi(market), activeLens, watchlistSymbols.join(','), portfolioSymbols.join(',')],
    enabled: activeLens !== null,
    queryFn: () => {
      if (!activeLens) throw new Error('lens is required');
      const params = new URLSearchParams({ market: marketToApi(market), lens: activeLens, topN: '8', preferStale: '1' });
      if (watchlistSymbols.length > 0) params.set('watchlistSymbols', watchlistSymbols.join(','));
      if (portfolioSymbols.length > 0) params.set('portfolioSymbols', portfolioSymbols.join(','));
      return fetchJSON<LensScanResult>(`/api/scan?${params.toString()}`);
    },
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: 1,
  });

  // 統一即時機會：intraday-opportunities (中-LLM) + lens-scan (快-L1) union → 同 ActionCard 樣式
  const intradayOpp = useQuery({
    queryKey: ['intraday-opportunities', marketToApi(market)],
    queryFn: () => fetchJSON<IntradayOpportunityApiResp>(`/api/intraday-opportunities?market=${marketToApi(market)}`),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: 1,
  });

  const unifiedIntradayPlans = useMemo(() => {
    const llmPlans = (intradayOpp.data?.decisions ?? []).map((d) =>
      intradayDecisionToPlan({
        symbol: d.symbol,
        action: d.action,
        entry: d.entry,
        stop: d.stop,
        t1: d.t1,
        t2: d.t2,
        confidence: d.confidence,
        reasonsTop3: d.reasonsTop3,
        judgeReasoning: d.judgeReasoning,
        asOf: intradayOpp.data?.asOf,
      })
    );
    const lensPlans = activeLens && lensScan.data?.candidates
      ? lensScan.data.candidates.map((c) => lensCandidateToPlan(c, activeLens))
      : [];
    return unifyOpportunities(llmPlans, lensPlans);
  }, [intradayOpp.data, lensScan.data, activeLens]);

  const adopt = useMutation({
    mutationFn: async (decision: Decision) => fetchJSON('/api/thesis/adopt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
      queryClient.invalidateQueries({ queryKey: ['theses'] });
      queryClient.invalidateQueries({ queryKey: ['managed-theses'] });
    },
  });

  const adoptLensCandidate = useMutation({
    mutationFn: async (candidate: LensCandidate) => {
      if (!activeLens) throw new Error('lens is required');
      if (!candidate.suggestedLevels) throw new Error('此快篩候選缺少進場 / 停損 / T1 / T2，無法寫入交易計畫。');
      const l1Score = candidate.scores['1m']?.score ?? candidate.scores['5m']?.score ?? candidate.scores.daily?.score;
      return fetchJSON('/api/trade-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: candidate.symbol,
          market: marketToApi(market),
          source: 'lens-scan',
          lensId: activeLens,
          l1ScoreAtPlan: l1Score,
          confidence: Math.min(0.95, Math.max(0.1, Math.abs(candidate.lensScore) / 10)),
          entryPrice: candidate.suggestedLevels.entry,
          stopLoss: candidate.suggestedLevels.stop,
          t1: candidate.suggestedLevels.t1,
          t2: candidate.suggestedLevels.t2,
          strategyProfile: settings.strategyProfile,
        }),
      });
    },
    onSuccess: (_data, candidate) => {
      setAdoptedLensSymbol(candidate.symbol);
      queryClient.invalidateQueries({ queryKey: ['trade-plans'] });
    },
  });

  const resetBreaker = useMutation({
    mutationFn: () => fetchJSON('/api/circuit-breaker/reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'manual-reset-from-ui-after-data-check' }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
    },
  });

  const weeklyHitRate = useMemo(() => {
    const rows = closedTheses.data?.theses ?? [];
    if (rows.length === 0) return '—';
    const wins = rows.filter((t) => (t.finalPnlPct ?? 0) > 0 || (t.realizedPnl ?? 0) > 0).length;
    return `${wins}/${rows.length} = ${Math.round((wins / rows.length) * 100)}%`;
  }, [closedTheses.data?.theses]);

  // 🐧 企鵝命中：近 30 天全體 TradePlan 結案勝率（命中率唯一準據）。
  const penguinHitRate = useMemo(() => {
    const rows = tradePlanSummary.data?.rows ?? [];
    const closed = rows.reduce((s, r) => s + (r.closed ?? 0), 0);
    const win = rows.reduce((s, r) => s + (r.win ?? 0), 0);
    if (closed === 0) return '—';
    return `${Math.round((win / closed) * 100)}%`;
  }, [tradePlanSummary.data?.rows]);

  const plan = dailyPlan.data ?? skeletonPlan.data;
  // 正在用 skeleton 撐版面、等全量 LLM 補強中（卡片框已出，內容慢慢浮現）
  const enriching = !dailyPlan.data && !!skeletonPlan.data && dailyPlan.isFetching;
  const mergedWatchlist = useMemo(() => mergeWatchlistPlans(plan, watchlistSymbols), [plan, watchlistSymbols]);
  // 進場機會「推薦補位池」：嚴格 newPicks（LLM buy）+ 掃描補位 scanFillers（含 avoid 大池，舊 cache fallback watchlist）。
  // 排除自選 symbol（自選走 mergedWatchlist 佔名額）。**合併 skeleton（limit 16 大池）的 scanFillers**：
  // 自選多到把 daily universe（limit 8）填滿、daily scanFillers 排除自選後幾乎空時，skeleton 大池仍提供非自選推薦，
  // 避免進場機會只剩自選、補不滿（user：異動自選沒補滿，根因＝自選數≈universe limit 把推薦擠光）。
  const planPicks = useMemo(() => {
    const watchSet = new Set(watchlistSymbols.map((s) => s.toUpperCase()));
    const picks = plan?.newPicks ?? [];
    const seen = new Set(picks.map((p) => p.decision.symbol.toUpperCase()));
    const fillerPool = [
      ...(plan?.scanFillers ?? plan?.watchlist ?? []),
      ...(skeletonPlan.data?.scanFillers ?? []),
    ];
    const fillers: ExecutionPlan[] = [];
    for (const p of fillerPool) {
      const sym = p.decision.symbol.toUpperCase();
      if (watchSet.has(sym) || seen.has(sym)) continue;
      seen.add(sym);
      fillers.push(p);
    }
    return [...picks, ...fillers];
  }, [plan?.newPicks, plan?.scanFillers, plan?.watchlist, skeletonPlan.data, watchlistSymbols]);
  // 「進場機會」與「觀望清單」合併成單一列表（卡片本身已標買進 / 觀望），版面更乾淨。
  // 合併時排除「我的部位」已持有的 symbol，並對 symbol 去重，避免已持有檔又被加進自選時
  // 在下方重複出現一張卡（盤中即時來源 unifiedIntradayPlans 不經後端 portfolio 去重）。
  const portfolioSymbolSet = useMemo(
    () => new Set((plan?.portfolioActions ?? []).map((p) => p.decision.symbol)),
    [plan?.portfolioActions],
  );
  // 研究台「AI 結論細條」資料源：依 active 股，從所有 decision 來源（部位 / 推薦 / 觀望 / 掃描池 / 盤中即時 / 自選）
  // 自動找該股的 plan 回報父層 —— 取代手動「研究台看依據」按鈕，研究台預設對「當前看的股票」顯示 AI 結論 + 分流依據。
  const activePlanForResearch = useMemo(() => {
    if (!activeSymbol) return null;
    const target = activeSymbol.toUpperCase();
    const sources = [
      ...(plan?.portfolioActions ?? []),
      ...(plan?.newPicks ?? []),
      ...(plan?.watchlist ?? []),
      ...(plan?.scanFillers ?? []),
      ...unifiedIntradayPlans,
      ...mergedWatchlist,
    ];
    return sources.find((p) => p.decision.symbol.toUpperCase() === target) ?? null;
  }, [activeSymbol, plan, unifiedIntradayPlans, mergedWatchlist]);
  useEffect(() => {
    onActiveDecisionChange?.(activePlanForResearch);
  }, [activePlanForResearch, onActiveDecisionChange]);
  const currentAccount = settings.buyingPower.accounts[marketToApi(market)];
  const currentCurrency: Currency = market === 'TW' ? 'TWD' : 'USD';
  const maxDeploy = currentAccount ? currentAccount.cash * (1 - currentAccount.reserveCashPct) * currentAccount.maxDeployPctPerDay : 0;

  return (
    <section className="space-y-2 mb-3">
      <div className="hero">
        <div className="hero-head">
          <div className="big">
            {plan?.marketAction === 'trade' ? '今天有可執行新單' : plan?.marketAction === 'manage-existing-only' ? '今天只管理既有部位' : '今天不開新倉'}
          </div>
          <div className="hero-actions">
            <button type="button" className="mini-btn" onClick={() => setBpOpen((v) => !v)} title="調整購買力 / 策略">
              ⚙ {bpOpen ? '收起設定' : '調整'}
            </button>
            <button
              type="button"
              className="mini-btn"
              onClick={() => { forceNextRefetch.current = true; dailyPlan.refetch(); }}
              disabled={dailyPlan.isFetching}
              title="跳過快取，重新取資料並評估"
            >
              <RefreshCw size={11} className={dailyPlan.isFetching ? 'animate-spin' : ''} /> 重新整理
            </button>
          </div>
        </div>
        <div className="why">
          {!plan && dailyPlan.isLoading ? '企鵝正在整理今日行動…' : dailyPlan.error && !plan ? '今日行動載入失敗，先不要跟單。' : enriching ? '技術骨架已就緒，AI 結論補強中…（卡片會自動更新）' : uiText(plan?.reason)}
        </div>
        <div className="stats">
          <HeroStat label="策略" value={strategyLabel(settings.strategyProfile)} />
          <HeroStat label="可動用資金" value={money(maxDeploy, currentCurrency)} />
          <HeroStat label="預期淨報酬" value={plan?.expectedNetReturnRange ? `${pctPoints(plan.expectedNetReturnRange[0])} ~ ${pctPoints(plan.expectedNetReturnRange[1])}` : '—'} />
          <HeroStat label="近期績效" value={weeklyHitRate} />
          <HeroStat label="🐧 企鵝命中" value={penguinHitRate} />
        </div>
        {plan && (() => {
          const model = modelInUse(plan);
          // llm-fallback-used 不再當警告顯示，改成直接秀「使用模型」。
          const flags = plan.warnings.filter((w) => w !== 'llm-fallback-used');
          if (!model && flags.length === 0) return null;
          return (
            <div className="flags">
              {model && (
                <span className="flag" title="本次今日計畫實際採用的 AI 模型（會在主力額度用完時自動切換到備援模型）。">
                  🤖 {model}
                </span>
              )}
              {flags.map((w) => {
                const f = flagDisplay(w);
                return <span key={w} className="flag" title={f.title}>⚠ {f.label}</span>;
              })}
            </div>
          );
        })()}
      </div>

      {bpOpen && (
        <BuyingPowerSettings value={settings} onChange={updateSettings} activeMarket={marketToApi(market)} open onClose={() => setBpOpen(false)} />
      )}

      <div>
        {!plan && dailyPlan.isLoading && <Skeleton />}
        {dailyPlan.error && !plan && (
          <div className="rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-sm text-(--text-secondary)">
            <span className="font-bold text-(--warn)">⚠ AI / API 暫時忙碌：</span>
            {(dailyPlan.error as Error).message}
          </div>
        )}

        {plan && (
          <>
            {enriching && (
              <div className="mb-3 flex items-center gap-2 rounded border border-(--accent)/30 bg-(--accent)/10 px-3 py-1.5 text-[11px] text-(--text-secondary)">
                <RefreshCw size={12} className="animate-spin text-(--accent)" />
                先顯示技術骨架（L1 即時分數），企鵝正在補上 AI 結論，稍候卡片會自動更新。
              </div>
            )}
            {plan.circuitBreaker && plan.circuitBreaker.status === 'halt-new-trades' && !marketClosedToday(market) && (
              <div className="mb-3 rounded border border-(--down)/50 bg-(--down)/10 px-3 py-2 text-sm text-(--text-secondary)">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <ShieldAlert className="inline h-4 w-4 text-(--down) mr-1" />
                    <b className="text-(--down)">企鵝暫停推新單（安全保護）</b>
                    <div className="mt-1 text-[12px] leading-relaxed">{circuitBreakerExplanation(plan.circuitBreaker.reason)}</div>
                    {resetBreaker.error && <div className="mt-1 text-[11px] text-(--warn)">解除失敗：{(resetBreaker.error as Error).message}</div>}
                    <BreakerEventsDisclosure tone="down" />
                  </div>
                  <button
                    type="button"
                    onClick={() => resetBreaker.mutate()}
                    disabled={resetBreaker.isPending}
                    className="rounded border border-(--down)/50 px-2 py-1 text-[11px] font-semibold text-(--down) hover:bg-(--down)/10 disabled:opacity-60"
                  >
                    {resetBreaker.isPending ? '恢復中…' : '我已確認資料，恢復推單'}
                  </button>
                </div>
              </div>
            )}

            {plan.circuitBreaker && plan.circuitBreaker.status === 'manual-override' && (
              <div className="mb-3 rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--text-secondary) leading-relaxed">
                <ShieldAlert className="inline h-3.5 w-3.5 text-(--warn) mr-1" />
                <b>安全保護已由你手動關閉。</b>稍早企鵝偵測到資料異常（報價過舊或 AI 服務不穩定）而暫停推新單，你已確認資料正常並恢復推單。下方清單就是恢復後重算的結果，會繼續每 5 分鐘自動更新。
                <BreakerEventsDisclosure tone="warn" />
              </div>
            )}

            {(() => {
              const blocked = plan.newPicks.filter((p) => p.positionSizing.suggestedQuantity === 0);
              if (entryView !== 'plan' || blocked.length === 0 || plan.newPicks.length === 0) return null;
              const byConfidence = blocked.filter((p) => /要求信心至少/.test(p.positionSizing.reason));
              const byCash = blocked.filter((p) => !byConfidence.includes(p));
              return (
                <div className="mb-3 space-y-1.5">
                  {byConfidence.length > 0 && (
                    <div className="rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--text-secondary) leading-relaxed">
                      <b>🎯 {byConfidence.length} 筆 AI 候選但信心未達門檻：</b>
                      <span className="ml-1">{byConfidence.map((p) => `${p.decision.symbol} (${Math.round(p.decision.confidence * 100)}%)`).join('、')} — 卡片仍顯示完整分析；若想放寬，可在<b>購買力設定</b>把策略換成「swing」(要求 62%)。</span>
                    </div>
                  )}
                  {byCash.length > 0 && (
                    <div className="rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--text-secondary) leading-relaxed">
                      <b>💰 {byCash.length} 筆 AI 候選但購買力 / 風控擋住：</b>
                      <span className="ml-1">{byCash.map((p) => p.decision.symbol).join('、')} — 卡片仍顯示分析；可在<b>購買力設定</b>調高今日最多動用金額或開啟零股。</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {(() => {
              // 我的部位依「當前 market」只顯示對應幣別帳戶（台股 TWD / 美股 USD，不換匯不混算）。
              // 後端 portfolioActions 可能回傳跨市場部位，前端依 market 過濾，避免台股看到美股買的。
              const isTwMarket = market === 'TW';
              const mine = plan.portfolioActions.filter((p) => isTaiwanSymbol(p.decision.symbol) === isTwMarket);
              const count = mine.length;
              const acctLabel = isTwMarket ? '🇹🇼 台股 · TWD' : '🇺🇸 美股 · USD';
              return (
                <>
                  <div className="bar-title" data-sub>
                    <span className="ic">📊</span>我的部位
                    <span className="cnt">{acctLabel} ({count}) · 迷你 K 快速判斷該不該賣</span>
                    <span className="ln" />
                  </div>
                  <ActionSection
                    title="" hideTitle
                    empty={`目前${acctLabel}沒有進行中的部位；用「進場機會」卡片的買進，或到「計畫 / 跟單」的「我的交易計畫 → 新增持股」加入既有持股，都會出現在這裡。`}
                    plans={mine} kind="portfolio" onSelectSymbol={onSelectSymbol} costBySymbol={costBySymbol}
                  />
                </>
              );
            })()}

            {/* 進場機會：依市場狀態切顯示內容，不用 tab。
                user 邏輯：「今日計畫是盤前盤後看的，盤中行情跑掉就過舊」。
                - 市場開盤中 → 只顯示「即時」(中·LLM Top 5 + 快·L1 Lens 探索)，藏掉今日計畫
                - 盤前/盤後/休市 → 只顯示「今日計畫」(慢·完整 LLM debate)，藏掉即時 (盤外資料無意義)
                想看另一邊？切「強制顯示」開關。 */}
            <section className="mt-3">
              <div className="bar-title" data-sub>
                <span className="ic">❄️</span>進場機會
                <span className="cnt">
                  {entryView === 'intraday'
                    ? (marketOpenNow(market) ? '盤中即時 · 推薦保證名額 · 自選穿插' : '盤外即時（僅參考）')
                    : (marketOpenNow(market) ? '盤前計畫 · 盤中可能已偏離' : '今日計畫 · 推薦保證名額 · 自選穿插')}
                </span>
                <span className="ln" />
                <button
                  type="button"
                  onClick={() => setEntryView(entryView === 'intraday' ? 'plan' : 'intraday')}
                  title="切換顯示另一個（行情階段不同看的東西就不同）"
                  className="rounded border border-(--border) px-2 py-0.5 text-[10px] text-(--text-muted) hover:border-(--accent) hover:text-(--accent)"
                >
                  {entryView === 'intraday' ? '↻ 看今日計畫' : '↻ 看即時'}
                </button>
              </div>

              {/* 「不下單：策略要求信心至少 X」只在此寫一次，不再每張卡重複（user 回饋）。 */}
              <div className="mb-2 text-[10px] text-(--text-muted) leading-relaxed">
                ℹ️ 策略「{strategyLabel(settings.strategyProfile)}」要求信心 ≥ {settings.strategyProfile === 'day-swing' ? '0.60' : settings.strategyProfile === 'swing' ? '0.62' : '0.65'} 才會列為可下單；未達門檻仍顯示卡片供追蹤，不代表出場訊號。
              </div>

              {entryView === 'intraday' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                    <span className="text-(--text-muted)">技術快篩：</span>
                    {LENS_BUTTONS.map((btn) => (
                      <button
                        key={btn.id}
                        type="button"
                        onClick={() => setActiveLens(btn.id)}
                        title={btn.title}
                        className={`rounded border px-1.5 py-0.5 transition ${
                          activeLens === btn.id
                            ? 'border-(--accent) bg-(--accent)/15 text-(--accent)'
                            : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'
                        }`}
                      >
                        {btn.label}
                      </button>
                    ))}
                    <span className="ml-auto text-(--text-muted)">
                      🟦 中·LLM 判讀 + 🟪 快·L1 Lens 探索（依信心排序）
                    </span>
                  </div>
                  <OpportunityBoard
                    picks={unifiedIntradayPlans}
                    watch={mergedWatchlist}
                    portfolioSet={portfolioSymbolSet}
                    fillers={planPicks}
                    emptyText={
                      intradayOpp.isLoading || lensScan.isLoading
                        ? '載入即時機會中…'
                        : '盤外無即時機會；切「看今日計畫」看盤前 AI 推薦。'
                    }
                    onSelectSymbol={onSelectSymbol}
                    onAdopt={(decision) => adopt.mutate(decision)}
                    adopting={adopt.isPending}
                  />
                </div>
              )}
              {entryView === 'plan' && (
                <OpportunityBoard
                  picks={planPicks}
                  watch={mergedWatchlist}
                  portfolioSet={portfolioSymbolSet}
                  emptyText={plan.marketAction === 'no-trade' ? '今天 AI 精選清單全數觀望 / 避開；沒有符合進場條件的新單。' : '目前沒有符合購買力與風控的新單。'}
                  onSelectSymbol={onSelectSymbol}
                  onAdopt={(decision) => adopt.mutate(decision)}
                  adopting={adopt.isPending}
                />
              )}
            </section>

            <div className="mt-3 text-[10px] text-(--text-muted) leading-relaxed text-center">
              最終建議以本區塊為準；下方 K 線、技術、新聞、籌碼只作為依據。
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ───────── 進場機會：直立 3×2 / 寬 4×2；自選佔名額、推薦用信心最高補滿；第二排收合摘要 ─────────
type OppItem = { plan: ExecutionPlan; kind: ExecutionPlanSource };

/**
 * 進場機會第一排欄數：用 matchMedia 對齊 CSS .opps 的斷點（≥1280→4、≤640→1、其餘 3）。
 * 為何不再「量 grid 實際欄數」：ResizeObserver 寫法有生命週期 bug —— 首次 items 為空時 grid 還沒掛載
 * （observer 沒裝），資料晚到才掛載時不會重測；窄視窗載入後放大，observer 也不可靠地把 cols 從 1 改回 4。
 * 結果版面變成「1 大卡 + 一排小卡」而非滿排大卡（user 回報的「偶爾卡片大小不一致」）。
 * 改用與 CSS 一模一樣的 media query 字串 → JS 與 CSS 用同一套引擎判斷、永遠同步；監聽斷點 change 事件，
 * 跨斷點時即時更新，無元素依賴、無 chicken-and-egg、無 stale observer。
 */
const OPPS_COLS_MQ = ['(min-width: 1280px)', '(max-width: 640px)'] as const;
function computeOppsCols(): number {
  if (typeof window === 'undefined') return 4;
  if (window.matchMedia(OPPS_COLS_MQ[0]).matches) return 4;
  if (window.matchMedia(OPPS_COLS_MQ[1]).matches) return 1;
  return 3;
}
function useMeasuredCols(): number {
  const [cols, setCols] = useState(computeOppsCols);
  useEffect(() => {
    const update = () => setCols((prev) => { const next = computeOppsCols(); return prev !== next ? next : prev; });
    update(); // mount 時校正（初始 state 若因 SSR / 視窗變化不準，這裡修正）
    // 同時監聽 window resize（最普遍可靠）+ 斷點 change：跨斷點即時更新欄數。
    window.addEventListener('resize', update);
    const mqls = OPPS_COLS_MQ.map((q) => window.matchMedia(q));
    mqls.forEach((m) => m.addEventListener('change', update));
    return () => {
      window.removeEventListener('resize', update);
      mqls.forEach((m) => m.removeEventListener('change', update));
    };
  }, []);
  return cols;
}

/**
 * 組進場機會清單：推薦(picks=盤中即時 LLM/lens) 與自選(watch) 混排，**保證系統推薦至少佔 reserve 名額**
 * （預設 total 一半）→ 自選再多也不會把推薦完全擠光（修 user：盤中進場機會全是自選、看不到推薦）。
 * 推薦不足時用 L1 補位池(fillers，facts-only)墊底；自選不足時 watchSel 自動多顯示自選。最終全體按信心 desc。
 */
function buildOppLayout(
  picks: ExecutionPlan[],
  watch: ExecutionPlan[],
  portfolioSet: Set<string>,
  total: number,
  fillers: ExecutionPlan[] = [],
): OppItem[] {
  const byConfidence = (a: ExecutionPlan, b: ExecutionPlan): number =>
    (b.decision.confidence ?? 0) - (a.decision.confidence ?? 0);
  const seen = new Set<string>();
  const watchItems: OppItem[] = [];
  for (const p of watch) {
    const s = p.decision.symbol;
    if (portfolioSet.has(s) || seen.has(s)) continue;
    seen.add(s);
    watchItems.push({ plan: p, kind: 'watchlist' });
  }
  // 推薦：picks（盤中即時 LLM/lens，按信心排）優先、fillers（L1 補位池）墊底；排除自選/部位已佔的 symbol。
  const recItems: OppItem[] = [];
  for (const list of [picks, fillers]) {
    for (const p of [...list].sort(byConfidence)) {
      const s = p.decision.symbol;
      if (portfolioSet.has(s) || seen.has(s)) continue;
      seen.add(s);
      recItems.push({ plan: p, kind: 'new' });
    }
  }
  // 保證推薦至少佔 reserve 名額（有推薦時，預設 total 一半）→ 自選再多也不會把系統推薦擠光
  //（user：盤中進場機會全是自選、沒推薦）。推薦不足時 watchSel 自動補到足（自選多則多顯示自選）。
  const reserve = recItems.length > 0 ? Math.min(recItems.length, Math.max(1, Math.floor(total / 2))) : 0;
  const watchCap = Math.max(0, total - reserve);
  const watchSel = watchItems.slice(0, watchCap);
  const recSel = recItems.slice(0, Math.max(0, total - watchSel.length));
  const selected = [...watchSel, ...recSel];
  selected.sort((a, b) => (b.plan.decision.confidence ?? 0) - (a.plan.decision.confidence ?? 0));
  return selected;
}

interface PeekChartResp { candles: Array<{ timestamp: number; close: number; volume?: number }>; meta?: { previousClose?: number } }
/** 第二排摘要列的即時現價（與 MiniKLine 共用 chart 1m cache key，不額外重打）。 */
function usePeekLive(symbol: string): LiveQuote | null {
  const q = useQuery({
    queryKey: ['mini-line', symbol, '1m'],
    queryFn: () => fetchJSON<PeekChartResp>(`/api/chart/${encodeURIComponent(symbol)}?range=1d&interval=1m`),
    enabled: !!symbol,
    staleTime: 25_000,
    refetchInterval: 30_000,
    retry: 1,
  });
  return useMemo(() => computeSurge(q.data?.candles, q.data?.meta?.previousClose, symbol), [q.data, symbol]);
}

/** 第二排收合摘要列：代號/名稱/現價+漲跌/方向/信心 + 進停T1T2，含急漲跌邊框（低信心也掌握動向）。
 *  點卡片空白區 → 單獨展開成完整卡（onToggle）；點代號 → 切到該股研究台。 */
function OppPeekCard({ item, onSelectSymbol, onToggle }: { item: OppItem; onSelectSymbol: (s: string) => void; onToggle: () => void }) {
  const d = item.plan.decision;
  const companyName = useSymbolName(d.symbol);
  const live = usePeekLive(d.symbol);
  const isBuy = d.action === 'buy' || d.action === 'add';
  const isDown = d.action === 'exit' || d.action === 'reduce' || d.action === 'avoid';
  const noTrade = item.plan.actionStatus === 'no-trade';
  const tone = isBuy ? (noTrade ? 'skip' : 'buy') : isDown ? 'skip' : 'wait';
  const surge = live?.surge ?? null;
  const confidence = Math.round((d.confidence ?? 0) * 100);
  const chgCls = live == null ? 'flat' : live.dayPct > 0 ? 'up' : live.dayPct < 0 ? 'down' : 'flat';
  const lim = limitStatus(d.symbol, live?.dayPct);
  const actionIcon = isBuy ? '▲' : isDown ? '▼' : '◐';
  const kindCls = item.kind === 'new' ? 'kind-pick' : 'kind-watch';
  return (
    <div
      className={`opp-peek ${tone} ${kindCls} ${surge === 'up' ? 'surge-up' : surge === 'down' ? 'surge-down' : ''}`}
      onClick={onToggle}
      title="點一下展開完整卡"
    >
      <div className="opp-peek-r1">
        <button type="button" className="sym" onClick={(e) => { e.stopPropagation(); onSelectSymbol(d.symbol); }}>{d.symbol}</button>
        {companyName && companyName !== d.symbol && <span className="cname">{companyName}</span>}
        {item.kind === 'new' ? <span className="badge b-rec">推</span> : <span className="badge b-pick">自</span>}
        <span className={`badge ${isBuy ? 'b-buy' : isDown ? 'b-reduce' : 'b-wait'}`} title={actionLabel(d.action)}>{actionIcon}</span>
        {surge && <span className={`peek-surge ${surge}`}>{surge === 'up' ? '🔥 急漲' : '❄️ 急跌'}</span>}
        <span className="peek-conf">信心 {confidence}%</span>
      </div>
      <div className="opp-peek-r2">
        <span className={`px-now${lim ? ` locked ${lim}` : ''}`}>{live ? price(live.price, d.symbol) : '—'}</span>
        {lim ? <LimitTag status={lim} /> : live && <span className={`px-chg ${chgCls}`}>{live.dayPct >= 0 ? '+' : ''}{live.dayPct.toFixed(2)}%</span>}
        <span className="peek-lv">
          <i>進：{price(d.entry, d.symbol)}</i>
          <i className="down">停：{price(d.stop, d.symbol)}</i>
          <i className="up">T1：{price(d.t1, d.symbol)}</i>
          <i className="up">T2：{price(d.t2, d.symbol)}</i>
        </span>
      </div>
    </div>
  );
}

/** 進場機會看板：第一排完整 .act 卡 + 第二排可收合摘要（壓低高度）。 */
function OpportunityBoard({
  picks,
  watch,
  portfolioSet,
  emptyText,
  onSelectSymbol,
  onAdopt,
  adopting,
  fillers = [],
}: {
  picks: ExecutionPlan[];
  watch: ExecutionPlan[];
  portfolioSet: Set<string>;
  emptyText: string;
  onSelectSymbol: (s: string) => void;
  onAdopt: (d: Decision) => void;
  adopting: boolean;
  /** L1 補位池（facts-only 技術骨架）：picks 不足時墊底補滿 6/8，盤中 LLM 未就緒也不留空格。 */
  fillers?: ExecutionPlan[];
}) {
  const cols = useMeasuredCols();
  const total = cols * 2; // 第一排完整 + 第二排（收合）各一排
  // 第二排：整排一起展開 / 收合。點任一收合摘要卡 → 全展；點任一展開卡頂部「收合」→ 全收。
  const [showSecond, setShowSecond] = useState(false);
  const items = useMemo(() => buildOppLayout(picks, watch, portfolioSet, total, fillers), [picks, watch, portfolioSet, total, fillers]);
  if (items.length === 0) {
    return (
      <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-4 text-center text-[12px] text-(--text-muted)">
        {emptyText}
      </div>
    );
  }
  const full = items.slice(0, cols);
  const peek = items.slice(cols);
  // 展開時把第一排 + 第二排併成「單一 .opps grid」，所有卡同寬同欄、尺寸一致
  // （避免兩個獨立 grid 造成上下排卡片大小不一）；收合用底部單一按鈕，
  // 不在每張卡頭塞收合條（那會讓第二排卡比第一排高，看起來大小不一）。
  return (
    <div className="space-y-2.5">
      {peek.length > 0 && showSecond ? (
        <>
          <div className="grid opps">
            {items.map((it) => (
              <ActionCard key={`opp:${it.plan.decision.symbol}:${it.kind}`} plan={it.plan} kind={it.kind} onSelectSymbol={onSelectSymbol} onAdopt={onAdopt} adopting={adopting} />
            ))}
          </div>
          <button type="button" className="opp-collapse-btn" onClick={() => setShowSecond(false)}>
            ▲ 收合第二排機會
          </button>
        </>
      ) : (
        <>
          <div className="grid opps">
            {full.map((it) => (
              <ActionCard key={`opp:${it.plan.decision.symbol}:${it.kind}`} plan={it.plan} kind={it.kind} onSelectSymbol={onSelectSymbol} onAdopt={onAdopt} adopting={adopting} />
            ))}
          </div>
          {peek.length > 0 && (
            // 收合：每張摘要列，點任一 → 整排展開（onToggle → setShowSecond(true)）。
            <div className="opp-peek-wrap">
              {peek.map((it) => (
                <OppPeekCard key={`peek:${it.plan.decision.symbol}`} item={it} onSelectSymbol={onSelectSymbol} onToggle={() => setShowSecond(true)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** 我的部位精簡小卡（mockup .pos）：迷你K + 現價 + 賣出判斷 + 4 格價位。 */
function PositionCard({ plan, onSelectSymbol, myCost }: { plan: ExecutionPlan; onSelectSymbol: (s: string) => void; myCost?: number }) {
  const [live, setLive] = useState<LiveQuote | null>(null);
  const d = plan.decision;
  const companyName = useSymbolName(d.symbol);
  const currency = currencyForSymbol(d.symbol);
  const action = d.action;
  const sellFlag = action === 'exit' || action === 'reduce' || action === 'avoid'
    ? { cls: 'warn', label: action === 'exit' ? '建議出場' : '考慮減碼' }
    : action === 'wait' ? { cls: 'hold', label: '續抱觀望' } : { cls: 'ok', label: '趨勢續多' };
  const surge = live?.surge ?? null;
  const surgeMag = live && surge ? surgeMagnitude(live) : 0;
  const posTone = live == null ? '' : live.dayPct > 0 ? 'up' : live.dayPct < 0 ? 'down' : 'wait';
  const pctCls = live == null ? 'flat' : live.dayPct > 0 ? 'up' : live.dayPct < 0 ? 'down' : 'flat';
  const lim = limitStatus(d.symbol, live?.dayPct);
  const cost = myCost ?? d.entry;
  const pnlPct = live && cost ? ((live.price - cost) / cost) * 100 : null;
  return (
    <article className={`pos ${posTone} ${surge === 'up' ? 'surge-up' : surge === 'down' ? 'surge-down' : ''}`}>
      {surge && live && (
        <span className={`surge-chip ${surge}`} title="近 3 根動能達門檻且最後一根同向（急漲/急跌）">
          {surge === 'up' ? '🔥 急漲' : '❄️ 急跌'} {surgeMag >= 0 ? '+' : ''}{surgeMag.toFixed(1)}%{isVolBurst(live) ? ` ⚡量x${live.volRatio.toFixed(1)}` : ''}
        </span>
      )}
      <div className="pos-top">
        <button type="button" className="sym" onClick={() => onSelectSymbol(d.symbol)}>{d.symbol}</button>
        <span className="badge b-trig">▲ {actionLabel(action)}</span>
        {lim ? <LimitTag status={lim} className="ml-auto" /> : <span className={`pct ${pctCls}`}>{live ? `${live.dayPct >= 0 ? '+' : ''}${live.dayPct.toFixed(2)}%` : '—'}</span>}
      </div>
      <div className="pos-sub">
        <span>{companyName ?? d.symbol}{pnlPct != null ? ` · ${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%` : ''}</span>
        <span className={`px-now${lim ? ` locked ${lim}` : ''}`}>{live ? price(live.price, d.symbol) : '—'} {currency}</span>
      </div>
      <MiniKLine symbol={d.symbol} levels={{ entry: d.entry, stop: d.stop, t1: d.t1, t2: d.t2, myCost }} onLive={setLive} />
      <div className="pos-sub"><span className={`sell-flag ${sellFlag.cls}`}>{sellFlag.label}</span></div>
      <div className="pos-lv">
        <div className="c"><div className="k">均</div><div className="v">{price(cost, d.symbol)}</div></div>
        <div className="c stop"><div className="k">停</div><div className="v">{price(d.stop, d.symbol)}</div></div>
        <div className="c t"><div className="k">T1</div><div className="v">{price(d.t1, d.symbol)}</div></div>
        <div className="c t"><div className="k">T2</div><div className="v">{price(d.t2, d.symbol)}</div></div>
      </div>
      <div className="pos-act">
        <button type="button" className="btn" onClick={() => onSelectSymbol(d.symbol)}>{action === 'add' ? '加碼' : '買進'}</button>
        <button type="button" className="btn btn-primary" onClick={() => onSelectSymbol(d.symbol)}>賣出</button>
      </div>
    </article>
  );
}

function ActionSection({
  title,
  empty,
  plans,
  kind,
  onSelectSymbol,
  onAdopt,
  adopting,
  compact = false,
  hideTitle = false,
  costBySymbol,
}: {
  title: string;
  empty: string;
  plans: ExecutionPlan[];
  kind: ExecutionPlanSource;
  onSelectSymbol: (symbol: string) => void;
  onAdopt?: (decision: Decision) => void;
  adopting?: boolean;
  compact?: boolean;
  hideTitle?: boolean;
  costBySymbol?: Record<string, number>;
}) {
  return (
    <section className={hideTitle ? '' : 'mt-3'}>
      {!hideTitle && <h3 className="mb-2 text-xs font-bold text-(--text-primary)">{title}</h3>}
      {plans.length === 0 ? (
        <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-4 text-center text-[12px] text-(--text-muted)">
          {empty}
        </div>
      ) : kind === 'portfolio' ? (
        <div className="pos-grid">
          {plans.map((plan) => (
            <PositionCard
              key={`pos:${plan.decision.symbol}`}
              plan={plan}
              onSelectSymbol={onSelectSymbol}
              myCost={costBySymbol?.[plan.decision.symbol]}
            />
          ))}
        </div>
      ) : (
        <div className={compact ? 'grid gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'space-y-2'}>
          {plans.map((plan) => (
            <ActionCard
              key={`${kind}:${plan.decision.symbol}:${plan.decision.action}:${plan.decision.modelUsed ?? 'local'}`}
              plan={plan}
              kind={kind}
              onSelectSymbol={onSelectSymbol}
              onAdopt={onAdopt}
              adopting={adopting}
              myCost={costBySymbol?.[plan.decision.symbol]}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ActionCard({
  plan,
  kind,
  onSelectSymbol,
  onAdopt,
  adopting,
  myCost,
}: {
  plan: ExecutionPlan;
  kind: ExecutionPlanSource;
  onSelectSymbol: (symbol: string) => void;
  onAdopt?: (decision: Decision) => void;
  adopting?: boolean;
  myCost?: number;
}) {
  const [live, setLive] = useState<LiveQuote | null>(null);
  const d = plan.decision;
  const currency = currencyForSymbol(d.symbol);
  const companyName = useSymbolName(d.symbol);
  const isBuy = d.action === 'buy' || d.action === 'add';
  const isDown = d.action === 'exit' || d.action === 'reduce' || d.action === 'avoid';
  const isPortfolio = kind === 'portfolio';
  const noTrade = plan.actionStatus === 'no-trade';
  const canAdopt = kind === 'new' && isBuy && plan.actionStatus !== 'no-trade' && plan.actionStatus !== 'stale-data-warning';
  const isSelfWatchlist = d.modelUsed == null && plan.positionSizing.reason === '自選觀察，不是可下單計畫。';
  const showStatusBadge = !(d.action === 'wait' && plan.actionStatus === 'pending-trigger');
  const confidence = Math.round((d.confidence ?? 0) * 100);
  // 急漲 / 急跌：拿 MiniKLine 回報的 surge（L1 即時層，非 LLM）。
  // 速度優先：近 5 分鐘急拉 / 急殺優先於當日累積，「突然下跌」能立刻變成急跌（見 lib/surge.ts）。
  const surge = live?.surge ?? null;
  const surgeMag = live && surge ? surgeMagnitude(live) : 0;
  const tone = isBuy ? (noTrade ? 'skip' : 'buy') : isDown ? 'reduce' : 'wait';
  const chgCls = live == null ? 'flat' : live.dayPct > 0 ? 'up' : live.dayPct < 0 ? 'down' : 'flat';
  const lim = limitStatus(d.symbol, live?.dayPct);
  const actionBadgeCls = isBuy ? 'b-buy' : isDown ? 'b-reduce' : 'b-wait';
  const actionBadgeIcon = isBuy ? '▲' : isDown ? '▼' : '◐';
  // 每卡重複的「不下單：策略要求信心至少 X」改成全區寫一次（見進場機會 bar-title 說明）；
  // 「觀望 / 避開：條件還沒成形…」這類制式句改放 action badge 的滑鼠懸停解釋，不佔 desc 一行。
  const summary = displayExecutionSummary(plan);
  const isBoilerplate = /^(不下單|觀望|避開)[:：]/.test(summary);
  // 買進卡下方已有 metrics（買多少/投入/最大虧/淨報酬），摘要句重複 → 不重複顯示。
  const hasMetrics = isBuy && !isSelfWatchlist && plan.positionSizing.suggestedQuantity > 0;
  const showDesc = !!summary && !isBoilerplate && !hasMetrics;
  const actionTitle = isBoilerplate ? summary : actionLabel(d.action);
  // 外框色條區分來源：自選(watchlist/self-watchlist) 金色、AI 推薦(new) 琥珀，一眼分辨。
  const kindCls = (kind === 'watchlist' || kind === 'self-watchlist') ? 'kind-watch' : kind === 'new' ? 'kind-pick' : '';

  return (
    <article className={`act ${tone} ${kindCls} ${surge === 'up' ? 'surge-up' : surge === 'down' ? 'surge-down' : ''}`}>
      {surge && live && (
        <span className={`surge-chip ${surge}`} title="近 3 根動能達門檻且最後一根同向（急漲/急跌）">
          {surge === 'up' ? '🔥 急漲' : '❄️ 急跌'} {surgeMag >= 0 ? '+' : ''}{surgeMag.toFixed(1)}%{isVolBurst(live) ? ` ⚡量x${live.volRatio.toFixed(1)}` : ''}
        </span>
      )}
      <div className="act-top">
        <div className="act-top-main">
          <div className="act-top-r1">
            <button type="button" className="sym" onClick={() => onSelectSymbol(d.symbol)}>{d.symbol}</button>
            {companyName && companyName !== d.symbol && (
              <span className="cname cursor-pointer" title={companyName} onClick={() => onSelectSymbol(d.symbol)}>{companyName}</span>
            )}
          </div>
          <div className="act-top-r2">
            {kind === 'new' ? (
              <span className="badge b-rec" title="AI 今日推薦">推</span>
            ) : (kind === 'watchlist' || kind === 'self-watchlist') ? (
              <span className="badge b-pick" title="自選清單">自</span>
            ) : null}
            <span className={`badge ${actionBadgeCls}`} title={actionTitle}>{actionBadgeIcon} {actionLabel(d.action)}</span>
            {showStatusBadge && <span className="badge b-trig">{displayStatusLabel(d.action, plan.actionStatus)}</span>}
            {!isSelfWatchlist && plan.positionSizing.affordability === 'reduced-size' && <span className="badge b-wait">降倉</span>}
          </div>
        </div>
        <div className="conf">
          {isSelfWatchlist ? (
            <><div className="n">自選</div><div className="l">來源</div></>
          ) : (
            <><div className="n">{confidence}%</div><div className="l">信心{d.riskReward != null ? ` · 風報 ${d.riskReward.toFixed(2)}` : ''}</div></>
          )}
        </div>
      </div>

      {showDesc && <div className="desc">{summary}</div>}

      {(kind === 'new' || kind === 'watchlist' || kind === 'self-watchlist' || kind === 'portfolio') && (
        <MiniKLine
          symbol={d.symbol}
          className="mt-2"
          levels={{ entry: d.entry, stop: d.stop, t1: d.t1, t2: d.t2, myCost: isPortfolio ? myCost : undefined }}
          onLive={setLive}
        />
      )}

      <div className={`px-bar ${chgCls}`}>
        <span className="lbl">現價</span>
        <span className={`px-now${lim ? ` locked ${lim}` : ''}`}>{live ? price(live.price, d.symbol) : '—'}</span>
        {lim ? <LimitTag status={lim} /> : live && <span className={`px-chg ${chgCls}`}>{live.dayPct >= 0 ? '+' : ''}{live.dayPct.toFixed(2)}%</span>}
      </div>

      <div className="levels">
        <div className="lv"><div className="k">{isPortfolio ? '均' : '進'}</div><div className="v">{price(isPortfolio ? (myCost ?? d.entry) : d.entry, d.symbol)}</div></div>
        <div className="lv stop"><div className="k">停</div><div className="v">{price(d.stop, d.symbol)}</div></div>
        <div className="lv t1"><div className="k">T1</div><div className="v">{price(d.t1, d.symbol)}</div></div>
        <div className="lv t2"><div className="k">T2</div><div className="v">{price(d.t2, d.symbol)}</div></div>
      </div>

      {isBuy && !isSelfWatchlist && plan.positionSizing.suggestedQuantity > 0 && (
        <div className="metrics">
          <div className="metric"><div className="k">買多少</div><div className="v">{plan.positionSizing.suggestedQuantity.toLocaleString()} {quantityUnit(plan.positionSizing.quantityUnit)}</div></div>
          <div className="metric"><div className="k">投入</div><div className="v">{amount(plan.positionSizing.suggestedNotional, currency)}</div></div>
          <div className="metric"><div className="k">最大虧</div><div className="v down">{amount(plan.positionSizing.maxLossAmount, currency)}</div></div>
          <div className="metric"><div className="k">淨報酬</div><div className="v up">{pctPoints(plan.tradeCost.expectedNetReturn[0])}~{pctPoints(plan.tradeCost.expectedNetReturn[1])}</div></div>
        </div>
      )}

      {!isSelfWatchlist && d.reasonsTop3 && d.reasonsTop3.length > 0 && (
        <div className="reasons">
          {d.reasonsTop3.slice(0, 3).map((r, i) => <div className="reason" key={i}>{r.point}</div>)}
        </div>
      )}

      {canAdopt && (
        <div className="mini-actions">
          <button type="button" className="btn btn-primary" onClick={() => onAdopt?.(d)} disabled={adopting}>採納成 thesis</button>
        </div>
      )}
    </article>
  );
}

/** 研究台頂部「AI 結論細條」：對當前 active 股預設顯示（App.tsx researchActive，TodayActionPanel 自動回報）。
 *  簡化 + 整合：細條放「結論 + 建議價位」（symbol / 行動 / 信心 / 一句話 summary ＋ 進·停·T1·T2）；
 *  詳細多空研判整合進「技術統計依據」卡、失敗條件 / 風險警告整合進「個股體質」卡，不在此重覆。 */
export function DecisionDetailPanel({ plan }: { plan: ExecutionPlan }) {
  const d = plan.decision;
  const isBuy = d.action === 'buy' || d.action === 'add';
  const isDown = d.action === 'exit' || d.action === 'reduce' || d.action === 'avoid';
  const confidence = Math.round((d.confidence ?? 0) * 100);
  const summary = displayExecutionSummary(plan);
  const isSelfWatchlist = d.modelUsed == null && plan.positionSizing.reason === '自選觀察，不是可下單計畫。';
  const actionBadgeCls = isBuy ? 'b-buy' : isDown ? 'b-reduce' : 'b-wait';
  const actionBadgeIcon = isBuy ? '▲' : isDown ? '▼' : '◐';
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-(--border) border-l-[3px] border-l-(--accent) bg-(--bg-soft) px-2.5 py-1.5 text-[12px]">
      <span className="text-[13px] font-extrabold text-(--text-primary)">{d.symbol}</span>
      <span className={`badge ${actionBadgeCls}`} title={actionLabel(d.action)}>{actionBadgeIcon} {actionLabel(d.action)}</span>
      {isSelfWatchlist ? (
        <span className="text-(--text-muted)">自選觀察、無 AI 計畫；依據看下方工作台 / 技術統計 / 個股體質</span>
      ) : (
        <>
          <span className="font-bold text-(--text-primary)">信心 {confidence}%{d.riskReward != null ? ` · 風報 ${d.riskReward.toFixed(2)}` : ''}</span>
          {summary && <span className="min-w-0 flex-1 truncate text-(--text-secondary)" title={summary}>{summary}</span>}
          <span className="shrink-0 text-[10px] text-(--text-muted)">完整多空 / 破壞點見下方技術統計 · 個股體質</span>
          {(d.entry != null || d.stop != null || d.t1 != null || d.t2 != null) && (
            <div className="w-full flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-(--border)/60 pt-1 text-[11px] tnum">
              <span className="font-semibold text-(--text-muted)">建議</span>
              <span><span className="text-(--text-muted)">進</span> <b className="text-(--text-primary)">{price(d.entry, d.symbol)}</b></span>
              <span><span className="text-(--text-muted)">停</span> <b className="text-(--down)">{price(d.stop, d.symbol)}</b></span>
              <span><span className="text-(--text-muted)">T1</span> <b className="text-(--up)">{price(d.t1, d.symbol)}</b></span>
              <span><span className="text-(--text-muted)">T2</span> <b className="text-(--up)">{price(d.t2, d.symbol)}</b></span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="k">{label}</div>
      <div className="v">{value}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) px-2 py-1.5">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className="text-[12px] font-bold text-(--text-primary) tnum">{value}</div>
    </div>
  );
}

/** 密排小卡常駐的迷你價位格（均/進 · 停 · T1 · T2），mockup-e .pos-lv 風格。 */
function LevelCell({ k, v, tone }: { k: string; v: string; tone?: 'stop' | 'target' }) {
  const color = tone === 'stop' ? 'text-(--down)' : tone === 'target' ? 'text-(--up)' : 'text-(--text-primary)';
  return (
    <div className="rounded border border-(--border) bg-(--bg-card) px-1 py-0.5 text-center">
      <div className="text-[9px] font-bold text-(--text-muted)">{k}</div>
      <div className={`text-[11px] font-bold tnum ${color}`}>{v}</div>
    </div>
  );
}

function statusLabel(status: ActionStatus): string {
  const map: Record<ActionStatus, string> = {
    'pending-trigger': '等觸發',
    triggered: '已觸發',
    adopted: '已採納',
    entered: '已進場',
    cancelled: '取消',
    expired: '過期',
    'stale-data-warning': '資料過舊',
    'no-trade': '不下單',
  };
  return map[status] ?? status;
}

function Skeleton() {
  // 刷新 / 首載時先把「我的部位 + 進場機會」的完整版面框架撐出來（第一排完整卡骨架 + 第二排收合摘要骨架），
  // 寬螢幕 4×（含第二排）、窄螢幕 3×，跟真版面一致；資料一到就替換成真卡片，不再整塊空白後蹦出。
  const wide = typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches;
  const cols = wide ? 4 : 3;
  return (
    <div className="space-y-3">
      <div className="bar-title" data-sub>
        <span className="ic">📊</span>我的部位<span className="cnt">載入中…</span><span className="ln" />
      </div>
      <div className="pos-grid">
        {Array.from({ length: cols }).map((_, i) => <div key={i} className="pos-skel skeleton" />)}
      </div>
      <div className="bar-title" data-sub>
        <span className="ic">❄️</span>進場機會<span className="cnt">載入中…</span><span className="ln" />
      </div>
      <div className="grid opps">
        {Array.from({ length: cols }).map((_, i) => <div key={i} className="act-skel skeleton" />)}
      </div>
      <div className="opp-peek-wrap">
        {Array.from({ length: cols }).map((_, i) => <div key={i} className="peek-skel skeleton" />)}
      </div>
    </div>
  );
}

/**
 * 自選快篩內容（無外框、無收合）—— 給 TodayActionPanel 進場機會 tab 使用，因為外層已經有 section 框 + tab 切換。
 * 共用 LENS_BUTTONS / LensCandidateCard。
 */
function LensQuickScanContent({
  market,
  activeLens,
  onSelectLens,
  scan,
  onSelectSymbol,
  onAdoptCandidate,
  adopting,
  adoptedSymbol,
  adoptError,
}: {
  market: Market;
  activeLens: LensId | null;
  onSelectLens: (lens: LensId) => void;
  scan: UseQueryResult<LensScanResult, Error>;
  onSelectSymbol: (symbol: string) => void;
  onAdoptCandidate: (candidate: LensCandidate) => void;
  adopting: boolean;
  adoptedSymbol: string | null;
  adoptError: Error | null;
}) {
  const result = scan.data;
  const marketLabel = market === 'TW' ? '台股' : '美股';
  const statusText = !activeLens
    ? '選一個快篩開始'
    : result?.refreshing || (scan.isFetching && !!result)
      ? '背景刷新中 / 顯示上次掃描結果'
      : scan.isLoading
        ? '背景刷新中'
        : result?.fromCache
          ? '快取命中'
          : result
            ? '最新掃描結果'
            : '尚無資料';

  return (
    <>
      <div className="mb-1 text-[11px] text-(--text-muted) leading-snug">
        自選快篩：L1 技術指標廣掃（不打 LLM）。選個 Lens 找候選，按「採納為計畫」會寫進交易計畫。{marketLabel} · {statusText}
      </div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {LENS_BUTTONS.map((btn) => (
          <button
            key={btn.id}
            type="button"
            onClick={() => onSelectLens(btn.id)}
            title={btn.title}
            className={`rounded border px-2 py-1 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
              activeLens === btn.id
                ? 'border-(--accent) bg-(--accent)/15 text-(--accent)'
                : 'border-(--border) text-(--text-secondary) hover:border-(--accent) hover:text-(--accent)'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {scan.isError && (
        <div className="mb-2 rounded border border-(--warn)/40 bg-(--warn)/10 px-2 py-1 text-[11px] text-(--warn)">
          Lens 掃描失敗：{(scan.error as Error).message}
        </div>
      )}

      {activeLens && scan.isLoading && !result && (
        <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-2 text-[11px] text-(--text-muted)">
          背景刷新中；冷啟動最多掃 50 檔，畫面不會卡住。🐧 正在把魚群雷達打開。
        </div>
      )}

      {result && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[10px] text-(--text-muted)">
            <span>
              asOf {formatLensTime(result.asOf)}
              {result.universe ? ` · universe ${result.universe.total}/${result.universe.max}` : ''}
              {result.warnings?.length ? ` · ${result.warnings.join(', ')}` : ''}
            </span>
            <span>{result.fromCache ? 'cache' : 'fresh'}{result.refreshing ? ' · refreshing' : ''}</span>
          </div>
          {result.candidates.length === 0 ? (
            <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-2 text-[11px] text-(--text-muted)">
              目前沒有符合這個 Lens 的候選；等下一輪資料刷新再看。
            </div>
          ) : (
            <div className="grid gap-1.5 md:grid-cols-2 xl:grid-cols-5">
              {result.candidates.slice(0, 5).map((candidate) => (
                <LensCandidateCard
                  key={`${result.lensId}:${candidate.symbol}`}
                  candidate={candidate}
                  onSelectSymbol={onSelectSymbol}
                  onAdoptCandidate={onAdoptCandidate}
                  adopting={adopting}
                  adopted={adoptedSymbol === candidate.symbol}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {adoptError && (
        <div className="mt-2 text-[11px] text-(--warn)">寫入 TradePlan 失敗：{adoptError.message}</div>
      )}
    </>
  );
}

function LensCandidateCard({
  candidate,
  onSelectSymbol,
  onAdoptCandidate,
  adopting,
  adopted,
}: {
  candidate: LensCandidate;
  onSelectSymbol: (symbol: string) => void;
  onAdoptCandidate: (candidate: LensCandidate) => void;
  adopting: boolean;
  adopted: boolean;
}) {
  const primaryScore = candidate.scores['1m']?.score ?? candidate.scores['5m']?.score ?? candidate.scores.daily?.score;
  return (
    <article className="rounded border border-(--border) bg-(--bg-base) px-2 py-2 text-[11px]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button type="button" onClick={() => onSelectSymbol(candidate.symbol)} className="font-black text-(--accent) hover:underline">
            {candidate.symbol}
          </button>
          <div className="truncate text-[10px] text-(--text-muted)">{candidate.name ?? candidate.dataSource}</div>
        </div>
        <div className="text-right tnum">
          <div className="font-black text-(--text-primary)">{candidate.lensScore.toFixed(1)}</div>
          <div className={candidate.changePct >= 0 ? 'text-(--up)' : 'text-(--down)'}>{candidate.changePct >= 0 ? '+' : ''}{candidate.changePct.toFixed(2)}%</div>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <span className="rounded border border-(--border) px-1 py-0.5 text-[10px] text-(--text-muted)">L1 {primaryScore?.toFixed(1) ?? '—'}</span>
        <span className="rounded border border-(--border) px-1 py-0.5 text-[10px] text-(--text-muted)">source {candidate.dataSource.toUpperCase()}</span>
        <span className="rounded border border-(--border) px-1 py-0.5 text-[10px] text-(--text-muted)">{candidate.delayed ? 'delayed' : 'not delayed'}</span>
      </div>
      <div className="mt-1 text-[10px] text-(--text-muted) tnum">asOf {formatLensTime(candidate.asOf)}</div>
      {candidate.notes.length > 0 && (
        <div className="mt-1 truncate text-[10px] text-(--text-secondary)" title={candidate.notes.join(' · ')}>
          {candidate.notes.slice(0, 2).join(' · ')}
        </div>
      )}
      {candidate.suggestedLevels && (
        <div className="mt-1 text-[10px] text-(--text-muted) tnum">
          進 {price(candidate.suggestedLevels.entry, candidate.symbol)} / 停 {price(candidate.suggestedLevels.stop, candidate.symbol)} / T1 {price(candidate.suggestedLevels.t1, candidate.symbol)} / T2 {price(candidate.suggestedLevels.t2, candidate.symbol)}
        </div>
      )}
      {candidate.suggestedLevels && (
        <button
          type="button"
          onClick={() => onAdoptCandidate(candidate)}
          disabled={adopting || adopted}
          className="mt-2 w-full rounded border border-(--accent)/50 px-2 py-1 text-[10px] font-bold text-(--accent) hover:bg-(--accent)/10 disabled:opacity-60"
        >
          {adopted ? '已寫入 TradePlan' : adopting ? '寫入中…' : '採納為計畫'}
        </button>
      )}
    </article>
  );
}
